'use client';

/**
 * Standardized Table Cell Components
 *
 * Provides consistent styling for table headers and body cells across the platform.
 * Use these instead of raw MUI TableCell for visual consistency.
 */

import Box from '@mui/material/Box';
import { type SxProps, styled, type Theme } from '@mui/material/styles';
import TableCell, { type TableCellProps } from '@mui/material/TableCell';
import Typography from '@mui/material/Typography';
import type React from 'react';
import { transitionPresets } from '../../theme/transitions';
import { fontSizes, letterSpacings } from '../../theme/typography';
import { composeSx } from '../../utils/styledUtils';

// Stable ellipsis-truncation sx fragment — hoisted so React/Emotion see a
// stable reference across renders of StackedCell / TruncatedText (cells
// may render in hot table hot paths).
const TRUNCATE_SX = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const;

// ============================================================================
// Styled Components
// ============================================================================

export const StyledHeaderCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  fontSize: fontSizes['2xs'],
  textTransform: 'uppercase',
  letterSpacing: letterSpacings.wider,
  color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.55)' : 'rgba(0, 0, 0, 0.55)',
  borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
  padding: theme.spacing(0.875, 1.5),
  whiteSpace: 'nowrap',
  transition: transitionPresets.colors,
}));

/**
 * Styled body cell with slightly smaller font than default.
 * Font: 0.8125rem (13px)
 */
export const StyledBodyCell = styled(TableCell)(({ theme }) => ({
  fontSize: '0.8125rem',
  lineHeight: 1.4,
  color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.87)',
  borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.06)'}`,
  padding: theme.spacing(1, 1.5),
  transition: transitionPresets.colors,
}));

/**
 * Body cell with no-wrap for content like phone numbers, dates, IDs.
 */
export const StyledBodyCellNoWrap = styled(StyledBodyCell)({
  whiteSpace: 'nowrap',
});

// ============================================================================
// Component Wrappers (for easier prop typing)
// ============================================================================

export interface HeaderCellProps extends TableCellProps {
  /** Minimum width for the column */
  minWidth?: number | string;
}

export interface BodyCellProps extends TableCellProps {
  /** Prevent text wrapping */
  noWrap?: boolean;
}

/**
 * Header cell component with consistent styling.
 * Use for table headers to ensure uniform appearance.
 *
 * @example
 * <TableHead>
 *   <TableRow>
 *     <HeaderCell minWidth={180}>Borrower</HeaderCell>
 *     <HeaderCell>Email</HeaderCell>
 *     <HeaderCell minWidth={120}>Phone</HeaderCell>
 *   </TableRow>
 * </TableHead>
 */
export function HeaderCell({ minWidth, sx, ...props }: HeaderCellProps) {
  return (
    <StyledHeaderCell
      sx={{
        minWidth,
        ...sx,
      }}
      {...props}
    />
  );
}

/**
 * Body cell component with consistent styling.
 * Use for table body rows to ensure uniform appearance.
 *
 * @example
 * <TableBody>
 *   <TableRow>
 *     <BodyCell>{name}</BodyCell>
 *     <BodyCell>{email}</BodyCell>
 *     <BodyCell noWrap>{phone}</BodyCell>
 *   </TableRow>
 * </TableBody>
 */
export function BodyCell({ noWrap, sx, ...props }: BodyCellProps) {
  return (
    <StyledBodyCell
      sx={{
        whiteSpace: noWrap ? 'nowrap' : undefined,
        ...sx,
      }}
      {...props}
    />
  );
}

// ============================================================================
// Cell Content Helpers
// ============================================================================

export interface StackedCellProps {
  /** Top/primary line — typically the main value (e.g. street address, name). */
  primary: React.ReactNode;
  /** Bottom/secondary line — typically a muted qualifier (e.g. "Cape Coral, FL"). */
  secondary?: React.ReactNode;
  /** Truncate both lines with ellipsis. Defaults to true (cells are usually narrow). */
  truncate?: boolean;
  /**
   * Render the two values on a single row separated by a middot divider, instead of stacked.
   * Useful for narrow columns or inline use. Default false.
   */
  inline?: boolean;
  /** Optional sx overrides applied to the outer Box. */
  sx?: SxProps<Theme>;
}

const STACKED_ROOT_SX = { minWidth: 0, display: 'flex', flexDirection: 'column', gap: '1px' } as const;
const STACKED_PRIMARY_SX = { fontSize: '0.8125rem', lineHeight: 1.3, ...TRUNCATE_SX } as const;
const STACKED_PRIMARY_SX_WRAP = { fontSize: '0.8125rem', lineHeight: 1.3 } as const;
const STACKED_SECONDARY_SX = {
  fontSize: fontSizes.xs,
  color: 'text.secondary',
  lineHeight: 1.3,
  ...TRUNCATE_SX,
} as const;
const STACKED_SECONDARY_SX_WRAP = {
  fontSize: fontSizes.xs,
  color: 'text.secondary',
  lineHeight: 1.3,
} as const;

/**
 * Two-line cell content. Renders a primary value with a muted secondary line
 * below it. Use inside a `DataTable` cell renderer or a `BodyCell` to match
 * the canonical property-address pattern:
 *
 * @example
 * cell: (_v, row) => (
 *   <StackedCell primary={row.street} secondary={`${row.city}, ${row.state}`} />
 * )
 */
const INLINE_ROOT_SX = { display: 'flex', alignItems: 'baseline', gap: 0.5, minWidth: 0 } as const;

export function StackedCell({ primary, secondary, truncate = true, inline = false, sx }: StackedCellProps) {
  if (inline) {
    return (
      <Box sx={composeSx(INLINE_ROOT_SX, sx)}>
        <Typography variant="body2" component="span" sx={truncate ? STACKED_PRIMARY_SX : STACKED_PRIMARY_SX_WRAP}>
          {primary}
        </Typography>
        {secondary != null && secondary !== '' && (
          <>
            <Typography component="span" variant="body2" sx={{ color: 'text.disabled' }}>
              ·
            </Typography>
            <Typography
              variant="caption"
              component="span"
              sx={truncate ? STACKED_SECONDARY_SX : STACKED_SECONDARY_SX_WRAP}
            >
              {secondary}
            </Typography>
          </>
        )}
      </Box>
    );
  }
  return (
    <Box sx={composeSx(STACKED_ROOT_SX, sx)}>
      <Typography variant="body2" sx={truncate ? STACKED_PRIMARY_SX : STACKED_PRIMARY_SX_WRAP}>
        {primary}
      </Typography>
      {secondary != null && secondary !== '' && (
        <Typography variant="caption" sx={truncate ? STACKED_SECONDARY_SX : STACKED_SECONDARY_SX_WRAP}>
          {secondary}
        </Typography>
      )}
    </Box>
  );
}

export interface TruncatedTextProps {
  /** The text (or node) to truncate. */
  children: React.ReactNode;
  /** Explicit max width. Defaults to 100% of the cell. */
  maxWidth?: number | string;
  /** Optional tooltip title — defaults to the raw text when `children` is a string. */
  title?: string;
}

const TRUNCATED_FULL_SX = { display: 'block', maxWidth: '100%', ...TRUNCATE_SX } as const;

/**
 * Single-line ellipsis-truncated text for use inside table cells. Applies
 * `overflow: hidden; text-overflow: ellipsis; white-space: nowrap` with a
 * bounded maxWidth so long values like borrower names don't blow up a column.
 *
 * @example
 * cell: (_v, row) => <TruncatedText>{row.borrowerName}</TruncatedText>
 */
export function TruncatedText({ children, maxWidth = '100%', title }: TruncatedTextProps) {
  const resolvedTitle = title ?? (typeof children === 'string' ? children : undefined);
  const sx = maxWidth === '100%' ? TRUNCATED_FULL_SX : { display: 'block', maxWidth, ...TRUNCATE_SX };
  return (
    <Box component="span" title={resolvedTitle} sx={sx}>
      {children}
    </Box>
  );
}

export default { HeaderCell, BodyCell, StackedCell, TruncatedText };
